import React from "react";
import { useI18n } from "../i18n";
import { Target, Eye, Heart, Shield, Scale, Users } from "lucide-react";

export default function About() {
  const { lang } = useI18n();
  const pt = lang === "pt";

  const values = [
    { icon: Scale, t: pt ? "Zero conflito de interesse" : "Zero conflict of interest",
      d: pt ? "Não vendemos nenhum produto financeiro. Não ganhamos comissão. Não temos contratos com bancos ou corretoras." : "We sell no financial product. Earn no commission. Have no deals with banks or brokers." },
    { icon: Shield, t: pt ? "Privacidade como DNA" : "Privacy in our DNA",
      d: pt ? "Nunca pedimos CPF, patrimônio ou comprovante de renda. Só nome e e-mail." : "We never ask for ID, net worth or income proof. Just name and email." },
    { icon: Target, t: pt ? "Decisão-first" : "Decision-first",
      d: pt ? "Todo gráfico vem com um 'takeaway' em linguagem humana. Você sai sabendo o que fazer, não com 10 abas abertas." : "Every chart comes with a plain-language 'takeaway'. You leave knowing what to do, not with 10 open tabs." },
    { icon: Users, t: pt ? "Feito para pessoas reais" : "Made for real people",
      d: pt ? "Nosso público são médicos, atletas, empreendedores, influencers — não traders profissionais. Nada de jargão." : "Our audience is doctors, athletes, entrepreneurs, creators — not pro traders. No jargon." },
  ];

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-about">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-gold-500/30 bg-gold-500/5 px-3 py-1 text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
          {pt ? "Quem somos" : "Who we are"}
        </div>
        <h1 className="mt-6 font-display text-4xl sm:text-6xl tracking-tight leading-[1.02] max-w-4xl">
          {pt
            ? <>A primeira plataforma de inteligência financeira <em className="text-gold-300">que não quer o seu dinheiro.</em></>
            : <>The first financial intelligence platform <em className="text-gold-300">that doesn't want your money.</em></>
          }
        </h1>
        <p className="mt-6 max-w-2xl text-neutral-400 text-lg leading-relaxed">
          {pt
            ? "A GOLDORA nasceu de uma observação simples: pessoas que ganham bem são bombardeadas por assessores de banco, corretoras e \"gurus\" — todos eles com algo pra vender. Ninguém entrega informação limpa, global e comparável para que a pessoa decida sozinha. A gente entrega."
            : "GOLDORA was born from a simple observation: high earners are bombarded by bank advisors, brokers and \"gurus\" — all with something to sell. Nobody delivers clean, global, comparable information so the person can decide on their own. We do."
          }
        </p>
      </div>

      {/* Missão / Visão / Valores */}
      <div className="mt-14 grid md:grid-cols-2 gap-5">
        <div className="surface p-7 md:p-8" data-testid="about-mission">
          <div className="flex items-center gap-2.5 text-gold-400">
            <Target size={18} />
            <span className="text-xs uppercase tracking-[0.2em] font-mono">{pt ? "Missão" : "Mission"}</span>
          </div>
          <h2 className="mt-3 font-display text-2xl tracking-tight">
            {pt
              ? "Tornar decisões financeiras globais tão simples quanto escolher um restaurante."
              : "Make global financial decisions as simple as picking a restaurant."}
          </h2>
          <p className="mt-3 text-neutral-400 leading-relaxed">
            {pt
              ? "Democratizar o nível de inteligência que hoje só existe em terminais Bloomberg de $24 mil, entregando em linguagem humana, multi-idiomas, com IA e voz — para qualquer pessoa que ganhe bem mas não queira depender de assessor."
              : "Democratize the intelligence that today exists only in $24k Bloomberg terminals, delivering it in plain language, multi-lingual, with AI and voice — for anyone earning well but not wanting to depend on an advisor."}
          </p>
        </div>

        <div className="surface p-7 md:p-8" data-testid="about-vision">
          <div className="flex items-center gap-2.5 text-gold-400">
            <Eye size={18} />
            <span className="text-xs uppercase tracking-[0.2em] font-mono">{pt ? "Visão" : "Vision"}</span>
          </div>
          <h2 className="mt-3 font-display text-2xl tracking-tight">
            {pt
              ? "Em 5 anos, ser o \"segundo cérebro\" financeiro de 1 milhão de profissionais liberais, atletas e empreendedores no mundo."
              : "Within 5 years, be the financial \"second brain\" of 1 million liberal professionals, athletes and entrepreneurs worldwide."}
          </h2>
          <p className="mt-3 text-neutral-400 leading-relaxed">
            {pt
              ? "Uma rede global onde cada usuário toma decisões melhores — em casa, ações, cripto ou negócios — sem precisar passar pelo filtro de quem vive de comissão."
              : "A global network where every user makes better decisions — on homes, stocks, crypto or business — without the filter of someone living on commissions."}
          </p>
        </div>
      </div>

      {/* Values */}
      <div className="mt-10">
        <div className="flex items-center gap-2.5 text-gold-400">
          <Heart size={18} />
          <span className="text-xs uppercase tracking-[0.2em] font-mono">{pt ? "Valores" : "Values"}</span>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          {values.map((v) => {
            const Icon = v.icon;
            return (
              <div key={v.t} className="surface-elevated p-5">
                <div className="flex items-center gap-2 text-gold-300">
                  <Icon size={16} />
                  <span className="font-display text-lg">{v.t}</span>
                </div>
                <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{v.d}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Manifesto */}
      <div className="mt-14 surface p-7 md:p-10" data-testid="about-manifesto">
        <div className="text-xs uppercase tracking-[0.25em] text-gold-500 font-mono">◆ {pt ? "Manifesto" : "Manifesto"} ◆</div>
        <div className="mt-5 space-y-5 text-neutral-300 leading-relaxed max-w-3xl font-brand-sub text-[15px]" style={{letterSpacing:"0.01em"}}>
          <p>{pt
            ? "Acreditamos que informação financeira não deveria ser negociada em troca do seu dinheiro."
            : "We believe financial information should not be traded for your money."}</p>
          <p>{pt
            ? "Acreditamos que quem ganha bem merece decidir sozinho, sem ser empurrado para um fundo onde alguém ganha comissão."
            : "We believe high earners deserve to decide alone, without being pushed into a fund where someone earns a commission."}</p>
          <p>{pt
            ? "Acreditamos que um gráfico só serve quando vem com uma frase que explica o que ele quer dizer."
            : "We believe a chart only matters when it comes with a sentence explaining what it means."}</p>
          <p>{pt
            ? "Acreditamos que uma casa em Lisboa, um carro em Dubai e um ETF nos EUA deveriam caber no mesmo comparador."
            : "We believe a house in Lisbon, a car in Dubai and a US ETF should fit in the same comparator."}</p>
          <p className="text-gold-300 font-brand tracking-[0.18em] pt-3 border-t border-white/10">
            {pt
              ? "VISÃO GLOBAL. DECISÕES MAIS CLARAS."
              : "GLOBAL VISION. CLEARER DECISIONS."}
          </p>
        </div>
      </div>
    </div>
  );
}
